import {
  RISK_ESTIMATE_STATE_FILTER_VALUES,
  type AgentmeshRiskBucket,
  type AgentmeshScanCoverage,
} from '@kirin/api-client/contracts/extensions';

/**
 * Risk text when AgentMesh has no verdict yet (KIR-5208): unscanned, or not
 * in AgentMesh yet. Replaces "Not assessed" / "Unscanned". Not for a
 * built-in, which reads "Not applicable".
 */
export const NO_VERDICT_LABEL = 'No verdict yet';

/** Why a Risk cell reads "No verdict yet". */
export const NO_VERDICT_DESCRIPTION =
  'AgentMesh has not returned a verdict for this extension yet';

/**
 * Scan status and Risk text for a built-in without a verdict. Not "Unscanned"
 * or "No verdict yet": both promise a scan that never comes. Not "Built-in"
 * either: that is the Source column's value, not a scan status.
 */
export const NOT_APPLICABLE_LABEL = 'Not applicable';

/** Why a built-in reads "Not applicable" (matches the expanded row). */
export const NOT_APPLICABLE_DESCRIPTION =
  'Ships inside the IDE, and AgentMesh has no verdict for it';

/** The Risk cell's text per AgentMesh verdict bucket (AgentMesh's words). */
export const RISK_BUCKET_LABELS: Record<AgentmeshRiskBucket, string> = {
  dangerous: 'Dangerous',
  risky: 'Risky',
  clean: 'Clean',
};

/** What the Scan status cell shows: a coverage, or "Not applicable". */
export type ScanStatusDisplay = AgentmeshScanCoverage | 'not-applicable';

/** The Scan status cell's text. */
export const SCAN_STATUS_LABELS: Record<ScanStatusDisplay, string> = {
  scanned: 'Scanned',
  partially_scanned: 'Partially scanned',
  unscanned: 'Unscanned',
  manual_review: 'Manual review',
  'not-applicable': NOT_APPLICABLE_LABEL,
};

/**
 * What the Scan status cell shows (KIR-5208): the coverage the API sends;
 * without one, a built-in reads "Not applicable" (AgentMesh has no verdict
 * for it) and anything else "Unscanned".
 */
export function scanStatusOf(
  scanStatus: AgentmeshScanCoverage | null | undefined,
  isBuiltIn: boolean
): ScanStatusDisplay {
  return scanStatus ?? (isBuiltIn ? 'not-applicable' : 'unscanned');
}

/**
 * Whether both cells read "Not applicable": a built-in without a scan status.
 * The API sends none exactly when the built-in has no verdict.
 */
export function isNotApplicable(
  scanStatus: AgentmeshScanCoverage | null | undefined,
  isBuiltIn: boolean
): boolean {
  return scanStatusOf(scanStatus, isBuiltIn) === 'not-applicable';
}

/** 'high_risk' → 'High Risk' — human form of a raw AgentMesh label. */
export function formatRiskLabel(label: string): string {
  return label
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Risk filter option label: the text its cell shows. A saved "Not assessed"
 * value reads as "No verdict yet", which it now matches.
 */
export function riskFilterOptionLabel(value: string): string {
  if (
    value === RISK_ESTIMATE_STATE_FILTER_VALUES.NO_VERDICT ||
    value === RISK_ESTIMATE_STATE_FILTER_VALUES.NOT_ASSESSED
  ) {
    return NO_VERDICT_LABEL;
  }
  if (value === RISK_ESTIMATE_STATE_FILTER_VALUES.NOT_SCANNED_BUILT_IN) {
    return NOT_APPLICABLE_LABEL;
  }
  return value in RISK_BUCKET_LABELS
    ? RISK_BUCKET_LABELS[value as AgentmeshRiskBucket]
    : formatRiskLabel(value);
}

/** Scan status filter option label: the text its cell shows. */
export function scanStatusFilterOptionLabel(value: string): string {
  if (value === RISK_ESTIMATE_STATE_FILTER_VALUES.NOT_SCANNED_BUILT_IN) {
    return NOT_APPLICABLE_LABEL;
  }
  return value in SCAN_STATUS_LABELS
    ? SCAN_STATUS_LABELS[value as ScanStatusDisplay]
    : formatRiskLabel(value);
}

/**
 * Whether an extension's AgentMesh cells can read "Not applicable": it is
 * built-in, as its Source column shows (KIR-4830). Each version and the
 * collapsed row read Not applicable when, on top of that, the API sends no
 * scan status (no AgentMesh verdict).
 */
export function readsAsBuiltIn(extension: { isBuiltIn: boolean }): boolean {
  return extension.isBuiltIn;
}

/** Display names for AgentMesh's marketplace values (KIR-5361). */
const KNOWN_MARKETPLACE_LABELS: Record<string, string> = {
  vscode: 'VS Code Marketplace',
  openvsx: 'Open VSX',
};

/**
 * Where AgentMesh scanned the shown result from. Known values get a display
 * name; anything else is shown as AgentMesh sent it. Null when unknown.
 */
export function marketplaceLabel(
  marketplace: string | null | undefined
): string | null {
  const value = marketplace?.trim();
  if (!value) return null;
  return KNOWN_MARKETPLACE_LABELS[value.toLowerCase()] ?? value;
}
